import { decodeHead } from '../sequencingPlatformDetection.ts'

export type SequenceRead = { header: string; sequence: string }

export const DEFAULT_HEAD_READS = 200
/** Enough compressed bytes for ~200 paired 2×300 bp reads; ONT heads carry fewer, longer reads. */
const HEAD_BYTES = 128 * 1024

const parseFastq = (lines: string[], maxReads: number): SequenceRead[] => {
  const reads: SequenceRead[] = []
  const start = lines.findIndex((line) => line.startsWith('@'))
  if (start < 0) {
    return reads
  }
  /* a record is only trusted once its quality line arrived — the head cut may land inside it */
  for (let i = start; i + 3 < lines.length && reads.length < maxReads; i += 4) {
    if (!lines[i].startsWith('@') || !lines[i + 2].startsWith('+')) {
      break
    }
    reads.push({ header: lines[i].slice(1), sequence: lines[i + 1].toUpperCase() })
  }
  return reads
}

const parseFasta = (lines: string[], maxReads: number): SequenceRead[] => {
  const reads: SequenceRead[] = []
  let current: SequenceRead | null = null
  for (const line of lines) {
    if (line.startsWith('>')) {
      if (current) {
        reads.push(current)
      }
      if (reads.length >= maxReads) {
        return reads
      }
      current = { header: line.slice(1), sequence: '' }
    } else if (current) {
      current.sequence += line.toUpperCase()
    }
  }
  /* the open record is dropped — the head limit may have cut its sequence short */
  return reads
}

/** The first reads of a FASTQ/FASTA head, with sequences; an unrecognised text yields none. */
export const parseSequenceHead = (text: string, maxReads = DEFAULT_HEAD_READS): SequenceRead[] => {
  const lines = text.split('\n')
  if (!text.endsWith('\n')) {
    lines.pop()
  }
  const clean = lines.map((line) => (line.endsWith('\r') ? line.slice(0, -1) : line))
  const first = clean.find((line) => line.length > 0)
  if (first?.startsWith('@')) {
    return parseFastq(clean, maxReads)
  }
  if (first?.startsWith('>')) {
    return parseFasta(clean, maxReads)
  }
  return []
}

/** Never throws — an unreadable file simply yields no reads. */
export const readSequenceHead = async (file: Blob, maxReads = DEFAULT_HEAD_READS): Promise<SequenceRead[]> => {
  try {
    return parseSequenceHead(await decodeHead(file, HEAD_BYTES), maxReads)
  } catch (error) {
    console.warn('Could not read the head of a selected file for order-setting detection', error)
    return []
  }
}
